export type NameValidationResult = { valid: true } | { valid: false; error: string }

export const USERNAME_MIN_LENGTH = 3
export const USERNAME_MAX_LENGTH = 15
export const LEAGUE_NAME_MIN_LENGTH = 3
export const LEAGUE_NAME_MAX_LENGTH = 20
// A motto is optional; when set it wraps to at most two visual lines under the league name.
// The ~70-char cap plus the CSS two-line clamp keeps it inside that reserve.
export const LEAGUE_MOTTO_MAX_LENGTH = 70

const EMOJI_REGEX = /\p{Extended_Pictographic}/u
// Spaces are allowed: a user's initial display name is seeded from their Google account name
// (e.g. "John Smith"), so editing it must accept spaces too or they couldn't save their own name
// back. Leading/trailing space is trimmed before this check; the reserved-word guard below strips
// spaces so "a d m i n" is still caught.
const USERNAME_CHARSET_REGEX = /^[A-Za-z0-9_ ]+$/
const LEAGUE_NAME_CHARSET_REGEX = /^[A-Za-z0-9 '\-.]+$/

// Checked case-insensitively with spaces/underscores stripped, so e.g. "a_d_m_i_n" is caught too.
// Entries must therefore be space-free themselves. The spaced "Respect Football" never reaches
// here at all — 16 characters, so USERNAME_MAX_LENGTH rejects it first. `rwbet` stays reserved
// after the rename so nobody squats the old brand.
const RESERVED_USERNAMES = [
  'admin',
  'administrator',
  'moderator',
  'mod',
  'rwbet',
  'respectfootball',
  'respectfc',
  'support',
]

function stripSpacesAndUnderscores(value: string): string {
  return value.replace(/[\s_]/g, '')
}

export function validateUsername(raw: string): NameValidationResult {
  const value = raw.trim()

  if (value.length < USERNAME_MIN_LENGTH) {
    return { valid: false, error: 'Must be at least 3 characters' }
  }
  if (value.length > USERNAME_MAX_LENGTH) {
    return { valid: false, error: `Can't be longer than ${USERNAME_MAX_LENGTH} characters` }
  }
  if (EMOJI_REGEX.test(value)) {
    return { valid: false, error: "Can't contain emoji" }
  }
  if (!USERNAME_CHARSET_REGEX.test(value)) {
    return { valid: false, error: 'Use only letters, numbers, spaces, and underscores' }
  }
  // Reserved-word rejection deliberately reuses the generic message below — it must never hint
  // that a blocklist match (rather than e.g. a taken name) triggered the rejection.
  if (RESERVED_USERNAMES.includes(stripSpacesAndUnderscores(value).toLowerCase())) {
    return { valid: false, error: "That name isn't available" }
  }

  return { valid: true }
}

export function validateLeagueName(raw: string): NameValidationResult {
  const value = raw.trim()

  if (value.length < LEAGUE_NAME_MIN_LENGTH) {
    return { valid: false, error: 'Must be at least 3 characters' }
  }
  if (value.length > LEAGUE_NAME_MAX_LENGTH) {
    return { valid: false, error: `Can't be longer than ${LEAGUE_NAME_MAX_LENGTH} characters` }
  }
  if (EMOJI_REGEX.test(value)) {
    return { valid: false, error: "Can't contain emoji" }
  }
  if (!LEAGUE_NAME_CHARSET_REGEX.test(value)) {
    return { valid: false, error: 'Use only letters, numbers, and spaces' }
  }

  return { valid: true }
}

// Collapse any run of whitespace (including newlines) to a single space and trim. A motto is one
// logical line — the UI wraps it to at most two *visual* lines, so an embedded newline would break
// the fixed two-line reserve. Attribution (`— Shankly`) is part of the same string.
export function normalizeMotto(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim()
}

// A motto is optional (skippable at creation), so an empty string is valid. Only the length is
// constrained here; charset is intentionally permissive (punctuation, em dash for attribution).
export function validateLeagueMotto(raw: string): NameValidationResult {
  const value = normalizeMotto(raw)

  if (value.length > LEAGUE_MOTTO_MAX_LENGTH) {
    return { valid: false, error: `Can't be longer than ${LEAGUE_MOTTO_MAX_LENGTH} characters` }
  }

  return { valid: true }
}
